// core/utils/domainHelper.ts - Utilit´rios para gerenciamento de domínios

import { Tenant, DNSInstructions } from '../types';

/**
 * Gera um subdomínio único a partir do nome do negócio
 */
export const generateSubdomain = (businessName: string): string => {
  if (!businessName) return '';
  
  return businessName
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9]/g, '-') // Substitui caracteres especiais por hífens
    .replace(/-+/g, '-') // Remove múltiplos hífens consecutivos
    .replace(/^-|-$/g, '') // Remove hífens no início e fim
    .substring(0, 63); // Limite de 63 caracteres para subdomínio
};

/**
 * Valida se um domínio é válido
 */
export const isValidDomain = (domain: string): boolean => {
  const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
  return domainRegex.test(domain);
};

/**
 * Gera instruções DNS para configuração de domínio customizado
 */
export const generateDNSInstructions = (domain: string): DNSInstructions => {
  return {
    domain,
    records: [
      {
        type: 'A',
        name: '@',
        value: '151.101.1.195',
        ttl: 3600,
      },
      {
        type: 'A',
        name: '@',
        value: '151.101.65.195',
        ttl: 3600,
      },
      {
        type: 'CNAME',
        name: 'www',
        value: domain,
        ttl: 3600,
      },
      {
        type: 'TXT',
        name: '@',
        value: 'pescar-app-verification=npesca-portal-v2x4',
        ttl: 3600,
      },
    ],
    verificationToken: 'npesca-portal-v2x4',
    instructions: {
      pt: `
# Configuração de DNS para ${domain}

Siga os passos abaixo para configurar seu domínio:

## 1. Acesse o painel do seu registrador de domínios
(Registro.br, GoDaddy, HostGator, etc.)

## 2. Adicione os seguintes registros DNS:

### Registro A (aponta para Firebase Hosting):
- Tipo: A
- Nome/Host: @ (ou deixe em branco)
- Valor: 151.101.1.195
- TTL: 3600

### Registro A adicional:
- Tipo: A
- Nome/Host: @ (ou deixe em branco)
- Valor: 151.101.65.195
- TTL: 3600

### Registro CNAME (para www):
- Tipo: CNAME
- Nome/Host: www
- Valor: ${domain}
- TTL: 3600

### Registro TXT (para verificação):
- Tipo: TXT
- Nome/Host: @ (ou deixe em branco)
- Valor: pescar-app-verification=npesca-portal-v2x4
- TTL: 3600

## 3. Aguarde a propagação
A propagação DNS pode levar de 1 a 48 horas.

## 4. Verifique a configuração
Após configurar, clique no botão "Verificar DNS" no painel.
      `,
      en: `
# DNS Configuration for ${domain}

Follow these steps to configure your domain:

## 1. Access your domain registrar's panel
(Registro.br, GoDaddy, HostGator, etc.)

## 2. Add the following DNS records:

### A Record (points to Firebase Hosting):
- Type: A
- Name/Host: @ (or leave blank)
- Value: 151.101.1.195
- TTL: 3600

### Additional A Record:
- Type: A
- Name/Host: @ (or leave blank)
- Value: 151.101.65.195
- TTL: 3600

### CNAME Record (for www):
- Type: CNAME
- Name/Host: www
- Value: ${domain}
- TTL: 3600

### TXT Record (for verification):
- Type: TXT
- Name/Host: @ (or leave blank)
- Value: pescar-app-verification=npesca-portal-v2x4
- TTL: 3600

## 3. Wait for propagation
DNS propagation can take 1 to 48 hours.

## 4. Verify configuration
After configuring, click the "Verify DNS" button in the dashboard.
      `
    }
  };
};

/**
 * Gera token de verificação único para um domínio
 */
const generateVerificationToken = (_domain: string): string => {
  return 'npesca-portal-v2x4';
};

/**
 * Verifica se os registros DNS estão configurados corretamente
 */
export const verifyDNSConfiguration = async (domain: string): Promise<{
  verified: boolean;
  records: {
    type: string;
    verified: boolean;
    message?: string;
  }[];
}> => {
  // Em produção, isso seria uma Cloud Function que faz DNS lookup
  // Por enquanto, retorna um mock
  
  const records = [
    { type: 'A', verified: false, message: 'Pending verification' },
    { type: 'CNAME', verified: false, message: 'Pending verification' },
    { type: 'TXT', verified: false, message: 'Pending verification' },
  ];
  
  return {
    verified: false,
    records
  };
};

/**
 * Extrai o tenant ID do hostname atual
 */
export const extractTenantFromHostname = (hostname: string): string | null => {
  // Desenvolvimento
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('gitpod.io')) {
    return null;
  }
  
  // Sites Principais (Plataforma)
  const platformDomains = [
    'nossapesca.com',
    'www.nossapesca.com',
    'pescar.app',
    'www.pescar.app',
    'app-pescar.web.app',
    'app-pescar.firebaseapp.com'
  ];

  if (platformDomains.includes(hostname)) {
    return 'platform';
  }
  
  // Construtor
  if (hostname === 'construtor.nossapesca.com' || hostname === 'construtor.pescar.app') {
    return 'builder';
  }
  
  // Subdomínios de Sistema (.pescar.app ou .nossapesca.com)
  if (hostname.endsWith('.pescar.app') || hostname.endsWith('.nossapesca.com')) {
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      // Se for algo como sub.tenant.nossapesca.com, pegamos o tenant
      return parts[0]; 
    }
  }
  
  // Domínio customizado - o chamador deve resolver via Firestore
  return 'custom-domain';
};

/**
 * Formata domínio (remove protocolo, www, etc)
 */
export const normalizeDomain = (domain: string): string => {
  return domain
    .replace(/^https?:\/\//, '') // Remove protocolo
    .replace(/^www\./, '') // Remove www
    .replace(/\/$/, '') // Remove trailing slash
    .toLowerCase();
};
